import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ProductActions } from "./ProductActions";
import { useOwnerCurrency } from "@/hooks/useOwnerCurrency";
import { useState } from "react";
import { TablePagination } from "../ui/data-table/TablePagination";

interface ProductTableProps {
  sampleData: any[];
  onSaleClick: (item: any, isCredit?: boolean) => void;
  onRestock: (item: any) => void;
  onLoss: (item: any) => void;
  onDelete: (item: any) => void;
  onReturn: (item: any) => void;
}

export const ProductTable = ({
  sampleData,
  onSaleClick,
  onRestock,
  onLoss,
  onDelete,
  onReturn,
}: ProductTableProps) => {
  const { data: currency } = useOwnerCurrency();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  const totalPages = Math.ceil(sampleData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sampleData.slice(startIndex, endIndex);
  
  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'en stock':
        return 'bg-[#33C3F0]/10 text-[#33C3F0] border-[#33C3F0]';
      case 'stock bas':
        return 'bg-[#F97316]/10 text-[#F97316] border-[#F97316]';
      case 'rupture':
        return 'bg-[#ea384c]/10 text-[#ea384c] border-[#ea384c]';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-600';
    }
  };

  const getStatus = (quantity: number, minQuantity: number = 0) => {
    if (quantity === 0) {
      return "Rupture";
    } else if (quantity <= minQuantity) {
      return "Stock bas";
    }
    return "En stock";
  };

  const formatPrice = (price: string | number) => {
    if (typeof price === 'string') {
      const numericPrice = parseFloat(price.replace(/[^\d.-]/g, ''));
      return isNaN(numericPrice) ? `0 ${currency}` : `${numericPrice.toLocaleString()} ${currency}`;
    }
    return `${price.toLocaleString()} ${currency}`;
  };

  return (
    <div className="space-y-4">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nom du Produit</TableHead>
            <TableHead>Quantité</TableHead>
            <TableHead>Unité</TableHead>
            <TableHead>Prix Unitaire</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {currentItems.map((item) => {
            const minQuantity = item.data?.minQuantity || 0;
            const status = getStatus(item.quantity || 0, minQuantity);
            return (
              <TableRow key={item.id}>
                <TableCell>{item.data?.name || 'N/A'}</TableCell>
                <TableCell>{item.quantity || 0}</TableCell>
                <TableCell>{item.data?.unit || 'Pièce'}</TableCell>
                <TableCell>{formatPrice(item.data?.price || 0)}</TableCell>
                <TableCell>
                  <span className={`border px-2 py-1 rounded-full text-sm font-medium ${getStatusColor(status)}`}>
                    {status}
                  </span>
                </TableCell>
                <TableCell>
                  <ProductActions
                    item={item}
                    onSaleClick={onSaleClick}
                    onRestock={onRestock}
                    onLoss={onLoss}
                    onDelete={onDelete}
                    onReturn={onReturn}
                  />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      {totalPages > 1 && (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      )}
    </div>
  );
};